import {
  Component,
  OnDestroy,
  OnChanges,
  Input,
  SimpleChanges,
  Inject,
  ChangeDetectorRef,
  NgZone,
  PLATFORM_ID,
  OnInit,
} from "@angular/core";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { DashboardService } from "../dashboard.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { am5ChartLicense } from "../../../app.constants";
import { colorMapping } from "../pie-chart/pie-chart.model";
import { isPlatformBrowser } from "@angular/common";

@Component({
  selector: "app-single-bar-chart",
  templateUrl: "./single-bar-chart.component.html",
  styleUrl: "./single-bar-chart.component.scss",
})
export class SingleBarChartComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() startDate!: Date;
  @Input() endDate!: Date;
  @Input() categoryIds!: number[];
  @Input() companyId!: number;
  @Input() apiType: string = 'percentage'; // 'count' for /dashboardBookingsperStore, 'percentage' for /dashboardBookingsperStorePercentage
  isBarGraphZoom: boolean;
  @Input() chartContainerId: string =
    "lineDiv" + Math.random().toString(36).substring(2, 9); // Unique ID for chart container

  // Store data with colors for legend display
  storeData: { name: string; value: number; color: string }[] = [];

  // Helper method to convert HSL to RGB hex
  private hslToHex(h: number, s: number, l: number): string {
    s /= 100;
    l /= 100;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  }

  // Helper method to generate color dynamically based on index and total stores
    private getStoreColor(storeName: string, index: number, totalStores: number): string {
    // First check if store exists in colorMapping
    if (colorMapping[storeName]) {
      return colorMapping[storeName];
    }
    // Generate color dynamically based on the number of stores
    // Use HSL color space for better distribution
    const hue = Math.round((index / totalStores) * 360);
    const saturation = 60 + (index % 2) * 20; // Alternate between 60% and 80%
    const lightness = 45 + (index % 3) * 10; // Vary between 45%, 55%, 65%
    return this.hslToHex(hue, saturation, lightness);
  }

  private root: am5.Root | undefined;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private zone: NgZone,
    private dashboardService: DashboardService,
    private cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Detect changes in inputs and update the chart
    if (
      changes["categoryIds"] ||
      changes["companyId"] ||
      changes["apiType"] ||
      changes["startDate"]?.currentValue ||
      changes["endDate"]?.currentValue
    ) {
      this.loadChartData();
    }
  }

  ngOnInit(): void {
    const companyIdval = localStorage.getItem("companyId");
    this.companyId = companyIdval ? Number.parseInt(companyIdval, 10) : 0;
  }

  // Fetch data from API and bind to chart
  loadChartData() {
    const params = {
      categoryIds: this.categoryIds,
      startDate: this.startDate,
      endDate: this.endDate,
      companyId: this.companyId,
    };
    if (!params.startDate || !params.endDate) {
      return;
    }

    // Call appropriate API based on apiType
    const apiCall = this.apiType === 'count' 
      ? this.dashboardService.getDashboardBookingsStore(params)
      : this.dashboardService.getDashboardBookingsStorePercent(params);

    apiCall.subscribe(
      (response: any) => {
        // Dispose of the existing chart to avoid multiple root errors
        if (this.root) {
          this.root.dispose();
        }

        // Handle the response - could be an array directly or an object with 'value' property
        let data: any[] = [];
        if (Array.isArray(response)) {
          data = response.sort((a:any,b:any) => b.yValue - a.yValue);
        } else if (
          response &&
          typeof response === "object" &&
          Array.isArray(response.value)
        ) {
          data = response.value.sort((a: any, b: any) => b.yValue - a.yValue);;
        } else if (response === false || !response) {
          console.warn("No data available - invalid response");
          return;
        }

        // Ensure data is an array before processing
        if (!Array.isArray(data) || data.length === 0) {
          console.warn("No data available for the chart");
          return;
        }

        // Store data format: { xValue: storeName, yValue: percentageValue }
        const processedData = data.map((item: any) => ({
          xValue: item.xValue,
          yValue: item.yValue,
        }));

        // Populate storeData for legend display with colors
        // const totalStores = data.length;
        // this.storeData = data.map((item: any, index: number) => ({
        //   name: item.xValue,
        //   value: item.yValue,
        //   color: this.getStoreColor(item.xValue, index, totalStores)
        // }));

        // Trigger change detection to update the view
        this.cdr.detectChanges();

        this.createChart(processedData);
      },
      (error) => {
        this._snackBar.open(
          "Failed to load chart data. Please try again later.",
          "Close",
          {
            duration: 3000,
            horizontalPosition: "center",
            verticalPosition: "top",
          },
        );
        return;
      },
    );
  }

  createChart(data: any[]): void {
    this.browserOnly(() => {
      const element = document.getElementById(this.chartContainerId);
      if (!element) return;

      if (this.root) {
        this.root.dispose();
      }

      am5.addLicense(am5ChartLicense);

      this.root = am5.Root.new(this.chartContainerId);

      if (!this.root) {
        console.error("Root element is not defined");
        return;
      }

      this.root.setThemes([am5themes_Animated.new(this.root)]);
      this.root._logo?.dispose(); // Remove the logo to avoid clutter

      const chart = this.root.container.children.push(
        am5xy.XYChart.new(this.root, {
          panX: false,
          panY: false,
          wheelX: "none",
          wheelY: "none",
          paddingLeft: 0,
          layout: this.root.horizontalLayout,
        }),
      );

      chart.set(
        "scrollbarX",
        am5.Scrollbar.new(this.root, {
          orientation: "horizontal",
        }),
      );

      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(this.root, {
          categoryField: "xValue", // Use 'xValue' for the x-axis (store names)
          renderer: am5xy.AxisRendererX.new(this.root, {
            minorGridEnabled: true,
          }),
        }),
      );

      // Make X-axis labels more visible and readable
      xAxis.get("renderer").labels.template.setAll({
        fontSize: 11,
        rotation: -45,
        maxWidth: 100,
        textAlign: "center",
        centerX: am5.p100,
        centerY: am5.p50,
        oversizedBehavior: "wrap",
      });

      // Remove vertical grid lines by setting visibility to false
      xAxis.get("renderer").grid.template.setAll({
        visible: false,
      });

      xAxis.get("renderer").setAll({
        minGridDistance: 20,
      });
      // Set xAxis data based on the transformed data (store names on X-axis)
      xAxis.data.setAll(data);

      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(this.root, {
          min: 0,
          renderer: am5xy.AxisRendererY.new(this.root, {
            strokeOpacity: 0.1,
          }),
        }),
      );

      // Add a column series for the store values
      // this.makeColumnSeries("Bookings", "value", chart, xAxis, yAxis, data);
      this.makeColumnSeries("Bookings", "yValue", chart, xAxis, yAxis, data);
      // Add cursor for interaction
      chart.set("cursor", am5xy.XYCursor.new(this.root!, {}));

      chart.appear(1000, 100);
    });
  }

  makeColumnSeries(
    name: string,
    fieldName: string,
    chart: any,
    xAxis: any,
    yAxis: any,
    data: any[],
  ): void {
    if (!this.root) {
      console.error("Root element is not defined");
      return;
    }

    // Create the ColumnSeries (bar chart)
    const series = chart.series.push(
      am5xy.ColumnSeries.new(this.root, {
        name: name,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "yValue",
        categoryXField: "xValue",
        clustered: false,
      }),
    );

    // Column appearance
    series.columns.template.setAll({
      fillOpacity: 0.8,
      strokeOpacity: 0,
    });

    // Tooltip
    const tooltip = am5.Tooltip.new(this.root, {
      labelText: "{categoryX}: {valueY}",
    });

    series.set("tooltip", tooltip);

    // Set data
    series.data.setAll(data);
    xAxis.data.setAll(data); // ⭐ IMPORTANT
    series.bullets.push(() => {
      return am5.Bullet.new(this.root!, {
        locationY: 1,
        sprite: am5.Label.new(this.root!, {
          text: "{valueY}",
          centerX: am5.p50,
          centerY: am5.p0,
          populateText: true,
        }),
      });
    });
    
    // Apply colors dynamically based on the number of stores
    const totalStores = data.length;
    series.columns.template.adapters.add("fill", (fill: any, target: any) => {
      const dataItem = target.dataItem;

      if (dataItem) {
        const store = dataItem.dataContext.xValue;
        
        // First check if store exists in colorMapping
        if (colorMapping[store]) {
          return am5.color(colorMapping[store]);
        }
        
        // Generate dynamic color based on index and total stores
        const index = data.findIndex((d: any) => d.xValue === store);
        if (index !== -1) {
          const hue = Math.round((index / totalStores) * 360);
          const saturation = 60 + (index % 2) * 20;
          const lightness = 45 + (index % 3) * 10;
          const hexColor = this.hslToHex(hue, saturation, lightness);
          return am5.color(hexColor);
        }
      }

      return am5.color("#5AAF5C"); // fallback color
    });

     // Disables horizontal and vertical scrollbar
    chart.set("scrollbarX", null);
    chart.set("scrollbarY", null);
    series.appear();
  }

  ngOnDestroy() {
    if (this.root) {
      this.root.dispose(); // Properly dispose of the chart root to clean up resources
    }
  }

  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        f();
      });
    }
  }
}
